import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { VERIFY_EMAIL_PENDING_PATH, SIGNUP_PATH } from "../../utils/constants/routes";
import { saveSignupState, loadSignupState, clearSignupState } from "../../utils/helpers/signupState";
import { Stepper, StepHeader } from "./PlanScreen";
import Button from "../../components/Buttons/Button";
import InputField from "../../components/InputFields/InputField";
import ErrorMessage from "../../components/Messages/ErrorMessage";
import { POST } from "../../Apis/apiWrapper";
import { signupEndpoints } from "../../utils/constants/httpConstants";
import "./onboarding.css";

// Reason codes returned by check-subdomain
const REASON_MESSAGES = {
  taken:    "This subdomain is already taken. Please choose another.",
  reserved: "This subdomain is reserved and cannot be used.",
  dot:      "Subdomain cannot contain dots.",
  chars:    "Subdomain contains invalid characters.",
  empty:    "Please enter a subdomain.",
  error:    "Couldn't check availability. Please try again.",
};

const SubdomainScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData  = { ...loadSignupState(), ...(location.state || {}) };

  const [subdomain, setSubdomain]     = useState("");
  const [sdChecking, setSdChecking]   = useState(false);  // availability API in-flight
  const [sdAvail, setSdAvail]         = useState(null);   // { available, reason, tenant_url }
  const [isCreating, setIsCreating]   = useState(false);  // set-subdomain API in-flight
  const [createError, setCreateError] = useState("");
  const debounceRef = useRef(null);

  // Durable state: rehydrate / bounce to signup if no draft_id; keep persisted state current.
  useEffect(() => {
    if (!userData.draft_id) { navigate(SIGNUP_PATH, { replace: true }); return; }
    saveSignupState(userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subdomain validation: min 3 chars, valid pattern (run locally before calling API)
  const isLocalValid = subdomain.length >= 3 && /^[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]$/.test(subdomain);

  // Debounced availability check
  const checkAvailability = async (value) => {
    setSdChecking(true);
    setSdAvail(null);
    try {
      const res = await POST(signupEndpoints.checkSubdomain, { sub_domain: value });
      const d = res?.data?.data;
      setSdAvail({ available: d?.available, reason: d?.reason, tenant_url: d?.tenant_url });
    } catch {
      setSdAvail({ available: false, reason: "error" });
    } finally {
      setSdChecking(false);
    }
  };

  // Sanitize input + trigger debounced availability check
  const handleChange = (e) => {
    const raw = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "")
      .replace(/^-+/, "");
    setSubdomain(raw);
    setSdAvail(null);
    setCreateError("");

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (raw.length >= 3 && /^[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]$/.test(raw)) {
      debounceRef.current = setTimeout(() => checkAvailability(raw), 500);
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const previewUrl = subdomain
    ? `https://${subdomain}.reachiq.ai`
    : "https://yourcompany.reachiq.ai";

  const canCreate = isLocalValid && sdAvail?.available === true && !sdChecking && !isCreating;

  // Single derived status for the field feedback row (visibility: hidden when null)
  const sdFieldStatus = (() => {
    if (!subdomain) return null;
    if (subdomain.length < 3) return { type: "error", message: "Minimum 3 characters required" };
    if (!isLocalValid) return { type: "error", message: "Use letters and numbers only; it can't start or end with a hyphen." };
    if (sdChecking) return { type: "checking", message: "Checking availability…" };
    if (sdAvail?.available === true) return { type: "ok", message: "Available" };
    if (sdAvail?.available === false) return { type: "error", message: REASON_MESSAGES[sdAvail.reason] || "This subdomain is not available." };
    return null;
  })();

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreateError("");
    setIsCreating(true);
    try {
      const res = await POST(signupEndpoints.setSubdomain, {
        draft_id:   userData.draft_id,
        sub_domain: subdomain,
      });
      const d = res?.data?.data;

      // OAuth signup: the email is already verified by Google/Microsoft, so there is no
      // verification email to wait for. Finalize the tenant immediately (this also auto-logs
      // the SSO user in — cookies are set on the shared parent domain) and hand off to the
      // freshly-created workspace. Email/password signups continue to the "check your email" step.
      if (userData.oauth) {
        // Finalize now (email already verified by the provider). A RESOLVED response means the
        // tenant was created — whether auto-login succeeded (login payload) or degraded to the
        // "please sign in" fallback — so redirect to the workspace. A THROW (4xx/5xx) means the
        // tenant was NOT created (e.g. transaction abort, duplicate): surface it and let the user
        // retry the same draft — do NOT clear state or redirect to a subdomain that has no tenant.
        try {
          await POST(signupEndpoints.verifyEmail, { verification_token: d?.verification_token });
        } catch (finalErr) {
          const errs = finalErr?.data?.data?.errors || {};
          setCreateError(
            errs.email || errs.domain || errs.payment || finalErr?.data?.message ||
            "Could not finish setup. Please try again."
          );
          setIsCreating(false);
          return;
        }
        clearSignupState();
        window.location.href = "https://" + d?.tenant_url;
        return;
      }

      navigate(VERIFY_EMAIL_PENDING_PATH, {
        state: {
          ...userData,
          subdomain,
          tenant_url:         d?.tenant_url,
          verification_token: d?.verification_token,
        },
      });
    } catch (err) {
      const errors = err?.data?.data?.errors || {};
      setCreateError(
        errors.sub_domain || errors.payment || err?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="onb-page">
      <StepHeader stepNumber={3} />
      <Stepper current={3} />

      <div className="onb-subdomain-outer">
        <div className="onb-subdomain-card">
          <h2>Set up your workspace</h2>
          <p className="onb-sub">
            Choose a subdomain for your ReachIQ workspace. This will be your team's
            permanent URL. Pick something clear and professional.
          </p>

          <div className="onb-field">
            <label className="onb-label" htmlFor="workspace-subdomain">Your workspace URL</label>

            {/* DS InputField with the built-in isDomain suffix (renders the reachiq.ai
                domain label). Sanitization stays in handleChange; availability state is
                shown below. variant flips to "error" when the subdomain is unavailable. */}
            <InputField
              id="workspace-subdomain"
              size="md40"
              type="text"
              value={subdomain}
              onChange={handleChange}
              placeholder="yourcompany"
              autoComplete="off"
              spellCheck={false}
              isDomain
              variant={
                (isLocalValid && !sdChecking && sdAvail?.available === false) ||
                (subdomain.length > 0 && subdomain.length < 3) ||
                (subdomain.length >= 3 && !isLocalValid)
                  ? "error"
                  : "default"
              }
            />

            {/* Live URL preview */}
            <div className="onb-sd-preview">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
              </svg>
              Your workspace:&nbsp;
              <span className="onb-url">{previewUrl}</span>
            </div>

            {/* Field status — always rendered to reserve space; visibility:hidden when no active status */}
            <div style={{ height: "52px", marginTop: "8px", overflow: "hidden", visibility: sdFieldStatus ? "visible" : "hidden" }}>
              {sdFieldStatus?.type === "ok" ? (
                <div className="onb-sd-avail" role="status">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true" focusable="false">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Available
                </div>
              ) : sdFieldStatus?.type === "checking" ? (
                <div className="onb-sd-avail onb-sd-avail-checking" role="status">
                  Checking availability…
                </div>
              ) : (
                <div role="alert">
                  <ErrorMessage
                    message={sdFieldStatus?.message || " "}
                    marginBottom="0"
                    fontSize="12px"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Confirmation note */}
          <div className="onb-sd-confirmation">
            Once created, this subdomain becomes your team's login URL.
          </div>

          {createError && (
            <div role="alert">
              <ErrorMessage message={createError} marginBottom="10px" fontSize="12px" />
            </div>
          )}
          <div className="onb-sd-btn-wrap">
            <Button
              variant="primary"
              size="md40"
              label={isCreating ? "Creating workspace…" : "Create workspace"}
              onClick={handleCreate}
              disabled={!canCreate}
            />
          </div>

          {userData.email && !userData.oauth && (
            <p className="onb-sd-note">
              A verification link will be sent to <strong>{userData.email}</strong> immediately
              after creation.
            </p>
          )}
          {userData.email && userData.oauth && (
            <p className="onb-sd-note">
              Your email is already verified. Your workspace will be ready right after creation.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubdomainScreen;
